import React from 'react';
import { StyleProp, StyleSheet, Text, TextInput, TextStyle, View, ViewStyle } from 'react-native';
import { formatPhoneDisplay } from '../../sms/utils/phoneFormatting';
import { validatePhoneNumber } from '../../sms/utils/phoneValidation';

interface PhoneNumberInputProps {
  label: string;
  value: string;
  onChangeText: (digits: string) => void;
  placeholder?: string;
  required?: boolean;
  allowEmpty?: boolean;
  editable?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  inputStyle?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
}

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  label,
  value,
  onChangeText,
  placeholder = '(555) 123-4567',
  required = false,
  allowEmpty = true,
  editable = true,
  containerStyle,
  inputStyle,
  labelStyle,
}) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 10);
  const display = formatPhoneDisplay(digits);
  const validation = digits ? validatePhoneNumber(digits) : { ok: allowEmpty || !required, msg: '' };
  const showError = editable && !validation.ok && digits.length > 0;

  return (
    <View style={[styles.container, containerStyle]}>
      <Text style={[styles.label, labelStyle]}>
        {label}
        {required ? ' *' : ''}
      </Text>
      <TextInput
        style={[styles.input, showError && styles.inputError, inputStyle, !editable && styles.readOnly]}
        value={display}
        onChangeText={(text) => {
          const nextDigits = text.replace(/\D/g, '').slice(0, 10);
          onChangeText(nextDigits);
        }}
        placeholder={placeholder}
        placeholderTextColor="#999"
        keyboardType="phone-pad"
        editable={editable}
      />
      {showError && <Text style={styles.errorText}>{validation.msg}</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },
  input: {
    borderWidth: 1,
    borderColor: '#D0D0D0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 15,
    color: '#222',
    backgroundColor: '#fff',
  },
  inputError: {
    borderColor: '#DC2626',
  },
  errorText: {
    fontSize: 12,
    color: '#DC2626',
    marginTop: 4,
  },
  readOnly: {
    backgroundColor: '#F3F4F6',
    color: '#6B7280',
  },
});

export default PhoneNumberInput;
